import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";

import { useSelector } from "react-redux";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text, Icon } from "../elements";
import { formatAmountSuffix } from "../../utils/formatters";

export function BalanceSummaryChart() {
  const { theme } = useContext(ThemeContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const userDetails = useSelector((state) => state.user?.user);
  const companyId = userDetails?.company?.id;

  const dark = {
    border: "rgba(57, 73, 107, 0.6)",
    frame: "#2f3f61",
    title: "#f0f0f0",
    text: "#d1d1d1",
    bg: "rgba(27, 43, 77, 0.85)",
    hoverGlow: "rgba(35, 81, 175, 0.25)",
    rowBg: "rgba(255, 255, 255, 0.03)",
  };

  const light = {
    border: "rgba(209, 209, 209, 0.8)",
    frame: "#f0f0f0",
    title: "#403e57",
    text: "#5e5d72",
    bg: "#ffffff",
    hoverGlow: "rgba(35, 81, 175, 0.12)",
    rowBg: "rgba(0, 0, 0, 0.02)",
  };

  const colors = theme !== "light_mode" ? dark : light;

  const BALANCE_COLOR = "#2351af";
  const ADVANCE_COLOR = "#c71414";

  const fetchData = useCallback(async () => {
    try {
      if (!companyId) return;
      setLoading(true);
      const res = await AnalyticsService.getCompanyBalanceSummary(companyId);
      setData(res?.data || res || null);
    } catch (err) {
      console.error(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processChartData = () => {
    if (!data) return [];
    return [
      {
        name: "Clients",
        balance: parseFloat(data.clientsBalance || 0),
        advance: parseFloat(data.clientsAdvance || 0),
        icon: "people_outline",
      },
      {
        name: "Workers",
        balance: parseFloat(data.workersBalance || 0),
        advance: parseFloat(data.workersAdvance || 0),
        icon: "inventory_2",
      },
      {
        name: "Vendors",
        balance: parseFloat(data.vendorsBalance || 0),
        advance: parseFloat(data.vendorsAdvance || 0),
        icon: "storefront",
      },
      {
        name: "Subcontractors",
        balance: parseFloat(data.subcontractorsBalance || 0),
        advance: parseFloat(data.subcontractorsAdvance || 0),
        icon: "engineering",
      },
    ];
  };

  const chartData = processChartData();

  return (
    <Box className="mc-card" sx={{ mt: 2 }}>
      {/* Dynamic Pulse Animation CSS */}
      <style>
        {`
          @keyframes pulseGlow {
            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(35, 81, 175, 0.5); }
            70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(35, 81, 175, 0); }
            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(35, 81, 175, 0); }
          }
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      {/* Header */}
      <Box className="mc-card-header" style={{ display: "flex", justifyContent: "center" }}>
        <Heading
          as="h2"
          className="mc-card-title"
          style={{ fontSize: "18px", fontWeight: 600, paddingBottom: "6px", color: colors.title }}
        >
          Balance & Advance Summary
        </Heading>
      </Box>

      {/* Content Body */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : chartData.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: "12px",
            pt: 0.5,
          }}
        >
          {chartData.map((item, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: "12px",
                p: 1.4,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                backdropFilter: "blur(8px)",
                animation: `fadeInUp 0.4s ease-out ${index * 0.08}s both`,
                transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.03)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  borderColor: BALANCE_COLOR,
                  boxShadow: `0px 8px 20px ${colors.hoverGlow}`,
                  "& .icon-box": {
                    transform: "scale(1.08)",
                    backgroundColor: BALANCE_COLOR,
                    color: "#fff",
                  },
                  "& .material-icons": {
                    color: "#ffffff !important",
                  },
                },
              }}
            >
              {/* Icon & Title Header */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                <Box
                  className="icon-box"
                  sx={{
                    position: "relative",
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: theme !== "light_mode" ? "rgba(35, 81, 175, 0.18)" : "rgba(35, 81, 175, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Icon
                    className="material-icons"
                    style={{ color: BALANCE_COLOR, fontSize: "17px", transition: "color 0.3s ease" }}
                  >
                    {item.icon}
                  </Icon>
                  <Box
                    sx={{
                      position: "absolute",
                      top: -1,
                      right: -1,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: BALANCE_COLOR,
                      animation: "pulseGlow 2s infinite",
                    }}
                  />
                </Box>
                <Text
                  as="p"
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colors.title,
                    letterSpacing: "0.2px",
                  }}
                >
                  {item.name}
                </Text>
              </Box>

              {/* Data Rows */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                {/* Balance Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    p: "5px 9px",
                    borderRadius: "6px",
                    backgroundColor: colors.rowBg,
                    borderLeft: `3px solid ${BALANCE_COLOR}`,
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: theme !== "light_mode" ? "rgba(35, 81, 175, 0.12)" : "rgba(35, 81, 175, 0.05)",
                    },
                  }}
                >
                  <Text as="span" style={{ fontSize: "12px", fontWeight: "500", color: colors.text }}>
                    Balance
                  </Text>
                  <Heading as="h6" style={{ fontSize: "14px", fontWeight: "700", color: BALANCE_COLOR }}>
                    {formatAmountSuffix(item.balance, true)}
                  </Heading>
                </Box>

                {/* Advance Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    p: "5px 9px",
                    borderRadius: "6px",
                    backgroundColor: colors.rowBg,
                    borderLeft: `3px solid ${ADVANCE_COLOR}`,
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: theme !== "light_mode" ? "rgba(199, 20, 20, 0.12)" : "rgba(199, 20, 20, 0.05)",
                    },
                  }}
                >
                  <Text as="span" style={{ fontSize: "12px", fontWeight: "500", color: colors.text }}>
                    Advance
                  </Text>
                  <Heading as="h6" style={{ fontSize: "14px", fontWeight: "700", color: ADVANCE_COLOR }}>
                    {formatAmountSuffix(item.advance, true)}
                  </Heading>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Text style={{ color: colors.text }}>No balance data available</Text>
        </Box>
      )}
    </Box>
  );
}
